import { action, makeObservable, observable } from "mobx";
import TrainingCoursesApi from "../../../../../services/api/v2/trainingCourses/TrainingCourses.api";
import { TimeLineModel } from "../../../../../components/widgets/TimelineWidget/Timeline_model";
import moment from "moment";
import _ from "lodash";
import { TrainingCoursesListViewModel } from "../TrainingCoursesListView/TrainingCoursesListView_model";
import I18n from "../../../../../core/localization/I18n";
import { Tooltip } from "react-tippy";

export class TrainingTimelineReportModel {
  organisationId: number;
  projectId: number;
  timelineModel: TimeLineModel;
  trainingCourseModel: TrainingCoursesListViewModel;
  trainingCoursesIds: number[];
  trainingCoursesProvider = TrainingCoursesApi;
  @observable isLoading: boolean;
  @observable.ref timelineData = [];
  @observable.ref initialTimelineData = [];
  /**
   *
   */
  constructor(organisationId: number, projectId: number) {
    makeObservable(this);
    this.timelineModel = new TimeLineModel();
    this.organisationId = organisationId;
    this.projectId = projectId;
  }

  @action
  onMount = async () => {
    await this.getTrainingCourseData();
    await this.loadTimelineData();
    this.setIsLoading(false);
  };

  @action
  setTrainingCourseData = trainingCourseData => {
    this.trainingCoursesIds = trainingCourseData;
  };

  @action
  setTimelineData = timelineData => {
    this.timelineData = timelineData;
    if (timelineData.length === 0) {
      this.timelineModel.setIsLoading(false);
      return;
    }
    this.setTimelineDefaultDates(this.timelineData);
    this.setTimeLineItems(this.timelineData);
  };

  loadTimelineData = async () => {
    const trainingCourse = await this.trainingCoursesProvider.getTrainingTimelineReport(
      this.organisationId,
      this.projectId,
      this.trainingCoursesIds
    );

    if (!trainingCourse || trainingCourse.isError) return;

    this.setTimelineData(trainingCourse.payload);
  };

  @action
  getTrainingCourseData = async () => {
    let res = await this.trainingCoursesProvider.getGridData(this.organisationId, this.projectId);

    if (res.isError) {
      return;
    }
    const trainingCourseIds = res.payload.map(r => {
      return r.id;
    });
    this.setTrainingCourseData(trainingCourseIds);
  };

  @action
  setInitialTimelineData = data => {
    this.initialTimelineData = data;
  };

  @action
  setTrainingTimelineData = data => {
    this.timelineData = data;
  };

  @action
  setIsLoading = (isLoading: boolean) => {
    this.isLoading = isLoading;
  };

  @action
  setTimeLineItems = timelineData => {
    let groups = [];
    let items = [];

    timelineData.forEach(course => {
      const id = `${course.id}`;
      groups.push({
        id,
        start_time_v: moment(course.startDate).valueOf(),
        isParent: id.toString().split("_").length === 1,
        title:
          course.name.length > 16 ? (
            <Tooltip theme="light" followCursor html={<small className="d-block">{course.name}</small>}>
              {course.modules ? (
                <div style={{ paddingLeft: "25px" }}>
                  <strong>{course.name}</strong>
                </div>
              ) : (
                <div style={{ paddingLeft: "35px" }}>{course.name}</div>
              )}
            </Tooltip>
          ) : course.modules ? (
            <div style={{ paddingLeft: "25px" }}>
              <strong>{course.name}</strong>
            </div>
          ) : (
            <div style={{ paddingLeft: "35px" }}>{course.name}</div>
          )
      });
      course.modules.forEach(module => {
        const id = `${course.id}_${module.id}`;
        groups.push({
          id,
          start_time_v: moment(module.startDate).valueOf(),
          isParent: id.toString().split("_").length === 1,
          title:
            module.name.length > 16 ? (
              <Tooltip theme="light" followCursor html={<small className="d-block">{module.name}</small>}>
                {module ? (
                  <div style={{ paddingLeft: "25px" }}>
                    <strong>{module.name}</strong>
                  </div>
                ) : (
                  <div style={{ paddingLeft: "35px" }}>{module.name}</div>
                )}
              </Tooltip>
            ) : module ? (
              <div style={{ paddingLeft: "25px" }}>
                <strong>{module.name}</strong>
              </div>
            ) : (
              <div style={{ paddingLeft: "35px" }}>{module.name}</div>
            )
        });
      });
    });

    timelineData.forEach(course => {
      const id = `${course.id}`;
      items.push({
        id,
        group: id,
        title: course.name,
        start_time: moment(course.startDate),
        end_time: moment(course.endDate),
        data: course,
        isParent: id.toString().split("_").length === 1
      });

      course.modules.forEach(module => {
        const id = `${course.id}_${module.id}`;
        items.push({
          id,
          group: id,
          title: module.name,
          start_time: moment(module.startDate),
          end_time: moment(module.endDate),
          data: module,
          isParent: id.toString().split("_").length === 1
        });
      });
    });

    groups = _.sortBy(groups, (e: any) => [e.id, e.start_time_v]);

    this.timelineModel.setItems(items);
    this.timelineModel.setGroups(groups);
    this.timelineModel.setSidebarWidth(350);
    this.timelineModel.setSideheaderTitle(<strong className="pl-3 pb-1">{I18n.t("phrases.modulesCourses")}</strong>);
    this.timelineModel.itemRenderer = props => {
      const { item, getItemProps } = props;
      const action: FP.Entities.IActionSummary = item.data;
      const itemProps = getItemProps({});
      const top = parseInt(itemProps.style.top.replace("px", ""));
      itemProps.style = {
        ...itemProps.style,
        height: "12px",
        background: "#2196f3",
        lineHeight: "12px",
        top: top + 3 + "px",
        border: `1px solid #2196f3`
      };
      return (
        <Tooltip
          theme="light"
          followCursor
          html={
            <>
              <small className="d-block">{action.name}</small>
              <small className="d-block;">
                {I18n.t("table.startDate")}: {moment(action.startDate).format("L")}
              </small>
              <small className="d-block">
                {I18n.t("table.endDate")}: {moment(action.endDate).format("L")}
              </small>
            </>
          }
        >
          <div {...itemProps} title={null} />
        </Tooltip>
      );
    };
    this.timelineModel.groupRenderer = ({ group }) => {
      const className = !group.isParent ? "" : "highlighted";
      return <div className={className}>{group.title}</div>;
    };

    this.timelineModel.setIsLoading(false);
  };

  @action
  setTimelineDefaultDates = (timelineData: FP.Entities.IProgramme[] | FP.Entities.IProject[]) => {
    const proj = timelineData.map(e => {
      return {
        ...e,
        mStartDate: moment(e.actualStartDate),
        mEndDate: moment(e.actualEndDate)
      };
    });
    const minDate = _.minBy(proj, e => e.mStartDate);
    const maxDate = _.maxBy(proj, e => e.mEndDate);
    const startDate = minDate.mStartDate;
    const endDate = maxDate.mEndDate;
    this.timelineModel.setDefaultDates(startDate, endDate);
    this.timelineModel.setVisibleDates(startDate.subtract(10, "days"), endDate.add(10, "days"));
  };
}
